import React, {useMemo, useState} from 'react'
import {Marker} from 'react-map-gl/maplibre'
import styled from 'styled-components'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {ViewportState} from 'types'
import {getBoxShadow, useDidUpdate} from 'utils'
import {useWindowMode} from 'windows'
import convertUnit from 'lib/unit'
import {Pin, Paragraph, Map, ModalFullscreen, Icon} from 'common/components'
import {GiftShopTemplateDetailLocationProps} from './GiftShopTemplateDetailLocationProps'

const StyledModal = styled(ModalFullscreen)`
  z-index: 11;
`

const StyledTopSectionContainer = styled.div`
  position: absolute;
  top: ${convertUnit(20)};
  left: ${convertUnit(20)};
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  align-items: center;
  width: 100%;
`

const StyledCloseIcon = styled.div`
  z-index: 1;
  border-radius: 50%;
  top: ${convertUnit(25)};
  left: ${convertUnit(20)};
  width: ${convertUnit(40)};
  height: ${convertUnit(40)};
  aspect-ratio: 1;
  background-color: ${({theme}) => theme.white_3};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: ${({theme}) =>
    getBoxShadow(theme, {
      horizontalOffset: 0,
      verticalOffset: 2,
      blurRadius: 4,
      opacity: 0.24,
      color: 'black',
    })};
`

const StyledMarkIcon = styled.div`
  width: ${convertUnit(40)};
  height: ${convertUnit(40)};
  aspect-ratio: 1;
  border-radius: 50%;
  background-color: ${({theme}) => theme.white_4};
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledInfoLocation = styled.div`
  z-index: 1;
  top: ${convertUnit(25)};
  left: ${convertUnit(100)};
  padding: ${convertUnit(15)};
  height: auto;
  background-color: ${({theme}) => theme.white_1};
  display: flex;
  box-sizing: border-box;
  border-radius: ${convertUnit(16)};
  align-items: center;
  gap: ${convertUnit(16)};

  @media (min-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    margin-right: ${convertUnit(25)};
  }

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    top: auto;
    bottom: ${convertUnit(25)};
    left: ${convertUnit(25)};
    right: ${convertUnit(25)};
    position: absolute;
  }
`

const StyledParagraphContainer = styled.div`
  padding: ${convertUnit(5)};
  margin-right: ${convertUnit(15)};
`

const StyledMap = styled(Map)`
  position: relative;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
`

const StyledParagraph = styled(Paragraph)`
  overflow-wrap: anywhere;
`

export default function GiftShopTemplateDetailLocation({
  visible,
  location,
  handleClick,
}: GiftShopTemplateDetailLocationProps) {
  const mode = useWindowMode()

  const name = useMemo(() => location && location?.name, [location])
  const longitude = useMemo(() => location && location?.longitude, [location])
  const latitude = useMemo(() => location && location?.latitude, [location])

  const mapAttributeState = useState<ViewportState>({
    latitude: location?.latitude,
    longitude: location?.longitude,
    zoom: 15,
    maxZoom: 16,
    minZoom: 8,
  })
  const [mapAttr, setMapAttr] = mapAttributeState

  const renderMarker = useMemo(() => {
    if (latitude && longitude) {
      return (
        <Marker longitude={longitude} latitude={latitude}>
          <Pin />
        </Marker>
      )
    }
    return null
  }, [latitude, longitude])

  const handleRenderLocationInfo = useMemo(
    () => (
      <StyledInfoLocation>
        <StyledMarkIcon>
          <Icon type="location" color="danger_5" size={20} />
        </StyledMarkIcon>
        <StyledParagraphContainer>
          <StyledParagraph
            fontSize={mode === 'website' ? 'l' : 'm'}
            color="black"
            fontWeight="medium">
            {name === `lat: ${latitude} lng: ${longitude}`
              ? `${latitude}, ${longitude}`
              : name}
          </StyledParagraph>
          <StyledParagraph
            fontSize={mode === 'website' ? 'm' : 's'}
            color="gray_3"
            fontWeight="regular">
            {name === `lat: ${latitude} lng: ${longitude}`
              ? 'Latitude & Longitude'
              : `Lat: ${latitude}, Long: ${longitude}`}
          </StyledParagraph>
        </StyledParagraphContainer>
      </StyledInfoLocation>
    ),
    [latitude, longitude, mode, name],
  )

  const handleRenderTopSection = useMemo(
    () => (
      <StyledTopSectionContainer>
        <StyledCloseIcon onClick={handleClick}>
          <Icon type="close" color="primary_5" size={20} />
        </StyledCloseIcon>
        {mode === 'website' && handleRenderLocationInfo}
      </StyledTopSectionContainer>
    ),
    [handleClick, handleRenderLocationInfo, mode],
  )

  useDidUpdate(() => {
    if (mapAttr.latitude !== location?.latitude) {
      setMapAttr((prev) => ({
        ...prev,
        latitude: location?.latitude,
        longitude: location?.longitude,
      }))
    }
  }, [location])

  return (
    <StyledModal visible={visible}>
      {handleRenderTopSection}
      {mode !== 'website' && handleRenderLocationInfo}
      <StyledMap viewportState={mapAttributeState}>{renderMarker}</StyledMap>
    </StyledModal>
  )
}
